// Protocol v1. Only this module knows the gateway wire format; transports carry bytes.
export const MAX_VALUE = 256;
export function encodeMessage(type, id, payload = []) {
  const p = Uint8Array.from(payload), b = new Uint8Array(6 + p.length);
  if (b.length > 512) throw new Error('Gateway message too large');
  const v = new DataView(b.buffer); v.setUint16(0, b.length - 2, true);
  b[2] = 1; b[3] = type; v.setUint16(4, id, true); b.set(p, 6); return b;
}
export class GatewayClient {
  constructor(onFrame = () => {}, onTiming = () => {}) {
    this.onFrame = onFrame; this.onTiming = onTiming; this.queue = []; this.pending = null; this.serial = 0;
    this.buffer = []; this.epoch = 0; this.send = null;
  }
  attach(send) { this.close(); this.send = send; }
  close(reason = 'Gateway disconnected; pending write outcome may be unknown') {
    this.epoch++; this.send = null; this.buffer = [];
    const error = new Error(reason);
    this.pending?.reject(error); this.pending = null;
    this.queue.splice(0).forEach(r => r.reject(error));
  }
  request(type, payload = []) {
    if (!this.send) return Promise.reject(new Error('No gateway connected'));
    if (this.queue.length >= 64) return Promise.reject(new Error('Gateway request queue full'));
    return new Promise((resolve, reject) => {
      this.queue.push({ type, payload, resolve, reject }); this.pump();
    });
  }
  async pump() {
    if (this.pending || !this.send || !this.queue.length) return;
    const r = this.queue.shift(), epoch = this.epoch, started = Date.now();
    const id = this.serial = (this.serial % 65535) + 1;
    let timer;
    const response = new Promise((resolve, reject) => {
      this.pending = { id, resolve, reject };
      timer = setTimeout(() => reject(new Error('Gateway timeout; write outcome may be unknown')), 12000);
    });
    try {
      const [, value] = await Promise.all([this.send(encodeMessage(r.type, id, r.payload)), response]);
      this.onTiming(Date.now() - started);
      r.resolve(value);
    } catch (e) {
      // Stop this queued workload after a failure; never cascade or replay writes.
      if(epoch===this.epoch)this.queue.splice(0).forEach(waiting => waiting.reject(e));
      r.reject(e);
    }
    finally {
      clearTimeout(timer);
      if (epoch === this.epoch) { this.pending = null; this.pump(); }
    }
  }
  feed(chunk) {
    for (const byte of chunk) {
      this.buffer.push(byte);
      if (this.buffer.length < 2) continue;
      const size = this.buffer[0] + this.buffer[1] * 256 + 2;
      if (size < 6 || size > 512) { this.close('Invalid gateway framing'); throw new Error('Invalid gateway framing'); }
      if (this.buffer.length === size) {
        const b = Uint8Array.from(this.buffer); this.buffer = []; this.receive(b);
      }
    }
  }
  receive(b) {
    if (b[2] !== 1) throw new Error('Unsupported gateway version');
    const v = new DataView(b.buffer), id = v.getUint16(4, true);
    if (b[3] === 0x80 && this.pending?.id === id) {
      if (b.length < 7) throw new Error('Truncated gateway result');
      if (b[6] === 0) this.pending.resolve(b.slice(7));
      else {
        const names = {1:'Invalid request',2:'CANopen busy',3:'SDO timeout; write outcome may be unknown',4:'SDO abort',5:'CAN unavailable; write outcome may be unknown'};
        const code = b[6] === 4 && b.length === 11 ? v.getUint32(7,true) : null;
        const error = new Error(`${names[b[6]] || 'Gateway error'}${code === null ? '' : ` 0x${code.toString(16).padStart(8,'0')}`}`);
        error.abortCode = code; this.pending.reject(error);
      }
    } else if (b[3] === 0x81) {
      if (b.length < 7) throw new Error('Truncated telemetry');
      let offset = 7; const frames = [];
      for (let i = 0; i < b[6]; i++) {
        if (offset + 3 > b.length) throw new Error('Truncated CAN record');
        const canId = v.getUint16(offset,true), length = b[offset+2];
        if (canId > 0x7ff || length > 8 || offset + 3 + length > b.length) throw new Error('Invalid CAN record');
        frames.push([canId,b.slice(offset+3,offset+3+length)]); offset += 3+length;
      }
      if (offset !== b.length) throw new Error('Trailing telemetry bytes');
      frames.forEach(([canId,payload]) => this.onFrame(canId,payload));
    }
  }
  async hello() {
    const caps = await this.request(4);
    if (caps.length !== 3 || caps[0]+256*caps[1] < MAX_VALUE) throw new Error('Incompatible gateway capabilities');
    return caps;
  }
  address(node,index,sub) {
    if (!Number.isInteger(node)||node<1||node>127||!Number.isInteger(index)||index<0||index>65535||!Number.isInteger(sub)||sub<0||sub>255) throw new Error('Invalid SDO address');
    return [node,index&255,index>>>8,sub];
  }
  readSDO(node,index,sub=0) { return this.request(1,this.address(node,index,sub)); }
  writeSDO(node,index,sub,data) {
    if (!data.length || data.length>MAX_VALUE) return Promise.reject(new Error('SDO value must contain 1–256 bytes'));
    return this.request(2,[...this.address(node,index,sub),...data]);
  }
  subscribe(ids,interval=500,duration=60) {
    if(ids.length>32||new Set(ids).size!==ids.length||ids.some(id=>!Number.isInteger(id)||id<0||id>0x7ff)||!Number.isInteger(interval)||interval<50||interval>65535||!Number.isInteger(duration)||duration<0||duration>120) return Promise.reject(new Error('Invalid subscription'));
    return this.request(3,[interval&255,interval>>>8,duration&255,duration>>>8,ids.length,...ids.flatMap(id=>[id&255,id>>>8])]);
  }
}
